import { createRouter, createWebHistory } from 'vue-router'
import { isAuthenticated, restoreSession } from '@/composables/auth'
import ProjectsView from '../views/ProjectsView.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/',
      redirect: '/tasks'
    },
    {
      path: '/projects',
      name: 'projects',
      component: ProjectsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/projects/:id',
      name: 'project-detail',
      component: () => import('../views/ProjectDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/deadlines',
      name: 'deadlines',
      component: () => import('../views/DeadlinesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tasks',
      name: 'tasks',
      component: () => import('../views/TasksView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/schedule',
      name: 'schedule',
      component: () => import('../views/ScheduleView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/time-off',
      name: 'time-off',
      component: () => import('../views/TimeOffView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/holidays',
      name: 'holidays',
      component: () => import('../views/HolidaysView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/tags',
      name: 'tags',
      component: () => import('../views/TagsView.vue'),
      meta: { requiresAuth: true }
    }
  ],
})

// Track if session restoration is in progress
let sessionRestored = false
let sessionRestoring = null

// Restore session on app startup
export async function initializeAuth() {
  if (sessionRestoring) {
    return sessionRestoring
  }
  
  sessionRestoring = restoreSession()
  try {
    await sessionRestoring
    sessionRestored = true
  } finally {
    sessionRestoring = null
  }
}

// Set up auth guard that waits for session restoration
router.beforeEach(async (to, from, next) => {
  // Wait for session restoration to complete on first navigation
  if (!sessionRestored && !sessionRestoring) {
    await initializeAuth()
  } else if (sessionRestoring) {
    // If already in progress, wait for it
    await sessionRestoring
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const authenticated = isAuthenticated.value

  if (requiresAuth && !authenticated) {
    // Redirect to login if route requires auth and user is not authenticated
    next('/login')
  } else if (to.path === '/login' && authenticated) {
    // Redirect to /tasks if already logged in and trying to access login
    next('/tasks')
  } else {
    next()
  }
})

export default router
